import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { McpService } from "@/gen/omnios/v1/mcp_connect";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	ExternalLink,
	Loader2,
	Plus,
	Power,
	PowerOff,
	Trash2,
	Unplug,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/integrations")({
	component: IntegrationsSettingsPage,
});

const mcpClient = createClient(McpService, transport);

function IntegrationsSettingsPage() {
	const { t } = useTranslation("settings");

	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("integrations_breadcrumb") },
	]);

	const queryClient = useQueryClient();
	const [showAdd, setShowAdd] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["mcp-servers"],
		queryFn: () =>
			mcpClient.listMcpServers({
				page: { pageSize: 100, pageToken: "" },
			}),
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => mcpClient.deleteMcpServer({ id }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["mcp-servers"] });
		},
	});

	const toggleMut = useMutation({
		mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
			mcpClient.updateMcpServer({ id, enabled }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["mcp-servers"] });
		},
	});

	const oauthMut = useMutation({
		mutationFn: (serverId: string) => mcpClient.initiateMcpOAuth({ serverId }),
		onSuccess: (res) => {
			const w = 600;
			const h = 700;
			const left = window.screenX + (window.outerWidth - w) / 2;
			const top = window.screenY + (window.outerHeight - h) / 2;
			const popup = window.open(
				res.authorizationUrl,
				"mcp-oauth",
				`width=${w},height=${h},left=${left},top=${top}`,
			);
			if (popup) {
				const interval = setInterval(() => {
					if (popup.closed) {
						clearInterval(interval);
						queryClient.invalidateQueries({ queryKey: ["mcp-servers"] });
					}
				}, 500);
			}
		},
	});

	const disconnectMut = useMutation({
		mutationFn: (serverId: string) =>
			mcpClient.disconnectMcpOAuth({ serverId }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["mcp-servers"] });
		},
	});

	const servers = data?.servers ?? [];

	return (
		<div className="space-y-6">
			<div className="flex justify-end">
				<Dialog open={showAdd} onOpenChange={setShowAdd}>
					<DialogTrigger asChild>
						<Button size="sm">
							<Plus className="h-3.5 w-3.5 mr-1.5" />
							{t("integrations_add_server")}
						</Button>
					</DialogTrigger>
					<AddServerDialog onClose={() => setShowAdd(false)} />
				</Dialog>
			</div>

			{isLoading && (
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			)}

			{!isLoading && servers.length === 0 && (
				<Card className="px-4 py-8 text-center">
					<p className="text-sm text-muted-foreground">
						{t("integrations_empty")}
					</p>
				</Card>
			)}

			{servers.map((server) => (
				<Card key={server.id} className="px-4 py-4 space-y-3">
					<div className="flex items-start justify-between gap-3">
						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-2">
								<h3 className="text-sm font-medium truncate">{server.name}</h3>
								<span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground uppercase">
									{server.transportType}
								</span>
								{server.enabled ? (
									<span className="shrink-0 h-2 w-2 rounded-full bg-green-500" />
								) : (
									<span className="shrink-0 h-2 w-2 rounded-full bg-slate-300" />
								)}
							</div>
							<p className="text-xs text-muted-foreground mt-0.5 truncate">
								{server.transportType === "http"
									? server.url
									: [server.command, ...server.args].join(" ")}
							</p>
						</div>
						<div className="flex items-center gap-1">
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7"
								title={
									server.enabled
										? t("integrations_disable")
										: t("integrations_enable")
								}
								disabled={toggleMut.isPending}
								onClick={() =>
									toggleMut.mutate({
										id: server.id,
										enabled: !server.enabled,
									})
								}
							>
								{server.enabled ? (
									<Power className="h-3.5 w-3.5" />
								) : (
									<PowerOff className="h-3.5 w-3.5 text-muted-foreground" />
								)}
							</Button>
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7 text-destructive hover:text-destructive"
								title={t("common:delete")}
								disabled={deleteMut.isPending}
								onClick={() => deleteMut.mutate(server.id)}
							>
								<Trash2 className="h-3.5 w-3.5" />
							</Button>
						</div>
					</div>

					{server.oauthStatus !== "none" && (
						<div className="flex items-center gap-2 text-xs">
							<OAuthStatusBadge status={server.oauthStatus} />
							{server.oauthStatus === "connected" ? (
								<Button
									variant="outline"
									size="sm"
									className="h-6 text-xs"
									disabled={disconnectMut.isPending}
									onClick={() => disconnectMut.mutate(server.id)}
								>
									<Unplug className="h-3 w-3 mr-1" />
									{t("integrations_disconnect_oauth")}
								</Button>
							) : (
								<Button
									variant="outline"
									size="sm"
									className="h-6 text-xs"
									disabled={oauthMut.isPending}
									onClick={() => oauthMut.mutate(server.id)}
								>
									<ExternalLink className="h-3 w-3 mr-1" />
									{t("integrations_connect")}
								</Button>
							)}
						</div>
					)}
				</Card>
			))}
		</div>
	);
}

function OAuthStatusBadge({ status }: { status: string }) {
	const { t } = useTranslation("settings");
	const config: Record<string, { label: string; className: string }> = {
		pending: {
			label: t("integrations_oauth_pending"),
			className:
				"bg-yellow-100 text-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-400",
		},
		connected: {
			label: t("integrations_oauth_connected"),
			className:
				"bg-green-100 text-green-800 dark:bg-green-950/30 dark:text-green-400",
		},
		expired: {
			label: t("integrations_oauth_expired"),
			className:
				"bg-orange-100 text-orange-800 dark:bg-orange-950/30 dark:text-orange-400",
		},
		error: {
			label: t("integrations_oauth_error"),
			className: "bg-red-100 text-red-800 dark:bg-red-950/30 dark:text-red-400",
		},
	};

	const c = config[status] ?? {
		label: status,
		className: "bg-muted text-muted-foreground",
	};

	return (
		<span
			className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${c.className}`}
		>
			{c.label}
		</span>
	);
}

function AddServerDialog({ onClose }: { onClose: () => void }) {
	const { t } = useTranslation("settings");
	const queryClient = useQueryClient();

	const [name, setName] = useState("");
	const [transportType, setTransportType] = useState<"http" | "stdio">("http");
	const [url, setUrl] = useState("");
	const [command, setCommand] = useState("");
	const [argsStr, setArgsStr] = useState("");
	const [showOAuth, setShowOAuth] = useState(false);
	const [oauthClientId, setOauthClientId] = useState("");
	const [oauthClientSecret, setOauthClientSecret] = useState("");
	const [oauthAuthUrl, setOauthAuthUrl] = useState("");
	const [oauthTokenUrl, setOauthTokenUrl] = useState("");
	const [oauthScopes, setOauthScopes] = useState("");
	const [oauthRedirectUri, setOauthRedirectUri] = useState("");

	const createMut = useMutation({
		mutationFn: () =>
			mcpClient.createMcpServer({
				name,
				transportType,
				url: transportType === "http" ? url : "",
				command: transportType === "stdio" ? command : "",
				args:
					transportType === "stdio" && argsStr.trim()
						? argsStr.split(/\s+/)
						: [],
				oauthClientId: showOAuth ? oauthClientId : "",
				oauthClientSecret: showOAuth ? oauthClientSecret : "",
				oauthAuthUrl: showOAuth ? oauthAuthUrl : "",
				oauthTokenUrl: showOAuth ? oauthTokenUrl : "",
				oauthScopes:
					showOAuth && oauthScopes.trim()
						? oauthScopes.split(/[,\s]+/).filter(Boolean)
						: [],
				oauthRedirectUri: showOAuth ? oauthRedirectUri : "",
				env: {},
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["mcp-servers"] });
			onClose();
		},
	});

	const inputCls = "h-8";

	return (
		<DialogContent className="sm:max-w-lg">
			<DialogHeader>
				<DialogTitle>{t("integrations_add_title")}</DialogTitle>
				<DialogDescription>{t("integrations_add_desc")}</DialogDescription>
			</DialogHeader>

			<div className="space-y-4 py-2">
				<div className="space-y-1.5">
					<Label htmlFor="mcp-name">{t("integrations_label_name")}</Label>
					<Input
						id="mcp-name"
						className={inputCls}
						placeholder={t("integrations_name_placeholder")}
						value={name}
						onChange={(e) => setName(e.target.value)}
					/>
				</div>

				<div className="space-y-1.5">
					<Label>{t("integrations_label_transport")}</Label>
					<div className="flex gap-2">
						<Button
							type="button"
							variant="outline"
							className={`flex-1 ${
								transportType === "http"
									? "border-primary bg-primary/10 text-primary"
									: "border-input text-muted-foreground hover:bg-accent"
							}`}
							onClick={() => setTransportType("http")}
						>
							HTTP (SSE)
						</Button>
						<Button
							type="button"
							variant="outline"
							className={`flex-1 ${
								transportType === "stdio"
									? "border-primary bg-primary/10 text-primary"
									: "border-input text-muted-foreground hover:bg-accent"
							}`}
							onClick={() => setTransportType("stdio")}
						>
							stdio
						</Button>
					</div>
				</div>

				{transportType === "http" ? (
					<div className="space-y-1.5">
						<Label htmlFor="mcp-url">URL</Label>
						<Input
							id="mcp-url"
							className={inputCls}
							placeholder="https://mcp.example.com/sse"
							value={url}
							onChange={(e) => setUrl(e.target.value)}
						/>
					</div>
				) : (
					<>
						<div className="space-y-1.5">
							<Label htmlFor="mcp-cmd">{t("integrations_label_command")}</Label>
							<Input
								id="mcp-cmd"
								className={inputCls}
								placeholder="npx"
								value={command}
								onChange={(e) => setCommand(e.target.value)}
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="mcp-args">{t("integrations_label_args")}</Label>
							<Input
								id="mcp-args"
								className={inputCls}
								placeholder="-y @modelcontextprotocol/server-github"
								value={argsStr}
								onChange={(e) => setArgsStr(e.target.value)}
							/>
						</div>
					</>
				)}

				<div className="pt-1">
					<Button
						type="button"
						variant="ghost"
						className="text-xs text-muted-foreground hover:text-foreground h-auto p-0"
						onClick={() => setShowOAuth(!showOAuth)}
					>
						{showOAuth
							? t("integrations_oauth_hide")
							: t("integrations_oauth_show")}
					</Button>
				</div>

				{showOAuth && (
					<div className="space-y-3 rounded-lg border border-dashed p-3">
						<div className="grid grid-cols-2 gap-3">
							<div className="space-y-1.5">
								<Label htmlFor="oauth-cid">Client ID</Label>
								<Input
									id="oauth-cid"
									className={inputCls}
									value={oauthClientId}
									onChange={(e) => setOauthClientId(e.target.value)}
								/>
							</div>
							<div className="space-y-1.5">
								<Label htmlFor="oauth-cs">Client Secret</Label>
								<Input
									id="oauth-cs"
									type="password"
									className={inputCls}
									value={oauthClientSecret}
									onChange={(e) => setOauthClientSecret(e.target.value)}
								/>
							</div>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="oauth-auth">Authorization URL</Label>
							<Input
								id="oauth-auth"
								className={inputCls}
								placeholder="https://example.com/oauth/authorize"
								value={oauthAuthUrl}
								onChange={(e) => setOauthAuthUrl(e.target.value)}
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="oauth-tok">Token URL</Label>
							<Input
								id="oauth-tok"
								className={inputCls}
								placeholder="https://example.com/oauth/token"
								value={oauthTokenUrl}
								onChange={(e) => setOauthTokenUrl(e.target.value)}
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="oauth-scopes">
								{t("integrations_oauth_scopes_label")}
							</Label>
							<Input
								id="oauth-scopes"
								className={inputCls}
								placeholder="read,write"
								value={oauthScopes}
								onChange={(e) => setOauthScopes(e.target.value)}
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="oauth-redir">Redirect URI</Label>
							<Input
								id="oauth-redir"
								className={inputCls}
								placeholder={`${window.location.origin}/auth/mcp-callback`}
								value={oauthRedirectUri}
								onChange={(e) => setOauthRedirectUri(e.target.value)}
							/>
						</div>
					</div>
				)}
			</div>

			<DialogFooter>
				<Button variant="outline" size="sm" onClick={onClose}>
					{t("common:cancel")}
				</Button>
				<Button
					size="sm"
					disabled={
						createMut.isPending ||
						!name.trim() ||
						(transportType === "http" && !url.trim()) ||
						(transportType === "stdio" && !command.trim())
					}
					onClick={() => createMut.mutate()}
				>
					{createMut.isPending ? (
						<>
							<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							{t("integrations_creating")}
						</>
					) : (
						t("common:create")
					)}
				</Button>
			</DialogFooter>

			{createMut.isError && (
				<p className="text-sm text-destructive">
					{createMut.error instanceof Error
						? createMut.error.message
						: t("integrations_create_error")}
				</p>
			)}
		</DialogContent>
	);
}
